import clsx from 'clsx';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';

import { MODAL_TYPE } from '@/src/components/common/Elements/Modal/Config';
import ModalPanel from '@/src/components/common/Elements/Modal/ModalPanel/ModalPanel';
import Spinner from '@/src/components/common/Elements/Spinner/Spinner';
import { FileEntry } from '@/src/components/project/Inbox2/types';
import { useGetFolders } from '@/src/components/project/Library/api/getFolders';
import { useGetLibraryItem } from '@/src/components/project/Library/api/getLibraryItem';
import { TempBreadCrumbsDTO } from '@/src/components/project/Library/types';

import Folders from './Folders/Folders';

export function extractTemporaryBreadCrumbs(tree, targetId) {
  function dfs(node, path) {
    const newPath = [...path, { id: node.id, name: node.name }];
    if (node.id === targetId) return newPath;

    for (const child of node.children || []) {
      const result = dfs(child, newPath);
      if (result) return result;
    }

    return null;
  }

  for (const rootNode of tree) {
    const result = dfs(rootNode, []);
    if (result) return result;
  }

  return null; // Not found
}

function convertPathToBreadcrumb(path: string): string {
  return path.replace('/', ' > ');
}

function getArchiveHeaderTitle(
  libraryItemDetails: Record<string, any> | null | undefined,
  fileEntryPath: string | undefined
): string {
  if (libraryItemDetails?.['full-path']) {
    return `Archived to Library ${convertPathToBreadcrumb(libraryItemDetails['full-path'])}`;
  }

  if (fileEntryPath) {
    return `Archived to ${fileEntryPath}`;
  }

  return 'Archive to Library';
}

type ArchiveLibraryModalProps = {
  onClose: () => void;
  onSelect?: () => void;
  onSubmit: (
    destinationFolder: string,
    folderPath: string,
    tempBreadCrumbs: TempBreadCrumbsDTO
  ) => void;
  projectId: string;
  fileEntry: FileEntry;
};

const ArchiveLibraryModal = ({
  onClose,
  onSelect,
  onSubmit,
  projectId,
  fileEntry,
}: ArchiveLibraryModalProps) => {
  const [state, setState] = useState({
    selected: null,
    submitEnabled: false,
    folderPath: '',
  });
  const libraryItemQuery = useGetLibraryItem({
    libraryItemId: fileEntry.file.libraryLocation?.id || fileEntry.file.id,
    showError: false,
    config: {
      enabled: !!fileEntry.file.libraryLocation?.id,
    },
  });
  const libraryItemDetails = libraryItemQuery.data?.data;
  const fileBreadCrumbs = useMemo(() => {
    if (!fileEntry.file.libraryLocation.id && fileEntry.file.libraryLocation.tempBreadCrumbs) {
      return fileEntry.file.libraryLocation.tempBreadCrumbs;
    }

    return libraryItemDetails?.['bread-crumps'] || [];
  }, [libraryItemDetails, fileEntry.file.libraryLocation]);
  const fileParentFolder = fileBreadCrumbs[fileBreadCrumbs.length - 1];
  const breadcrumbsMap = new Map<number, string>(
    fileBreadCrumbs.map((item) => [item.id, item.name])
  );
  const foldersQuery = useGetFolders({ projectId: +projectId, inTrash: false, showError: false });
  const folders = foldersQuery.data?.data;

  const { submitEnabled, selected } = state;
  const headerTitle = getArchiveHeaderTitle(
    libraryItemQuery.data?.data,
    fileEntry.file.libraryLocation?.path
  );

  const handleSelect = (folder, folderPaths) => {
    const submitEnabled = folder !== null;

    setState((prevState) => ({
      ...prevState,
      submitEnabled: submitEnabled,
      selected: folder,
      folderPath: 'Library > ' + folderPaths.join(' > '),
    }));

    if (onSelect) {
      onSelect();
    }
  };

  const handleSubmit = () => {
    onSubmit(selected, state.folderPath, extractTemporaryBreadCrumbs(folders, selected.id));
  };

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      selected: fileParentFolder,
    }));
  }, [fileParentFolder]);

  if (libraryItemQuery.error || foldersQuery.error) {
    return (
      <ModalPanel
        key={headerTitle}
        id={'modal--archiveInboxEntry'}
        title={'Archive to Library'}
        modalType={MODAL_TYPE.default_bg}
        onClose={onClose}
        onConfirm={handleSubmit}
        confirmBtn={{ label: 'Move Here', disabled: !submitEnabled }}
        containerClasses={clsx('tw-pt-[225px] !tw-mr-[60px]')} // (!) is not removed here as it is to override the styles from vendor.css
      >
        <span className="tw-flex tw-pl-[50px] tw-pt-[20px]">
          Please coordinate with your CloudCfo team to request access to this library.
        </span>
      </ModalPanel>
    );
  }

  if (!folders || foldersQuery.isLoading) {
    return <Spinner />;
  }

  return (
    <ModalPanel
      key={headerTitle}
      id={'modal--archiveInboxEntry'}
      title={headerTitle}
      modalType={MODAL_TYPE.default_bg}
      onClose={onClose}
      onConfirm={handleSubmit}
      confirmBtn={{ label: 'Move Here', disabled: !submitEnabled }}
      containerClasses={clsx('tw-pt-[225px] !tw-mr-[60px]')} // (!) is not removed here as it is to override the styles from vendor.css
    >
      {/* {foldersQuery.isLoading && <Spinner />} */}
      <div
        className={clsx(
          'wrapper--folderList',
          'tw-h-[calc(100vh-400px)] tw-overflow-y-auto tw-scroll-styles'
        )}
      >
        <Folders
          projectId={projectId}
          folders={folders}
          selected={selected}
          document={fileEntry}
          onSelect={handleSelect}
          savedParentFolderId={fileParentFolder?.id}
          breadcrumbsMap={breadcrumbsMap}
          parentFolders={[]}
        />
      </div>
    </ModalPanel>
  );
};

export default ArchiveLibraryModal;
